import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  makeStyles
} from "@material-ui/core";
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
  Minimize2,
  Delete,
  Activity
} from "lucide-react";
import { GlobalSoftphoneContext } from "../../context/GlobalSoftphone/GlobalSoftphoneContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";

const BUTTON_POS_KEY = "softphone_fab_position";

const useStyles = makeStyles((theme) => ({
  fabContainer: {
    position: "fixed",
    zIndex: 9998,
    cursor: "grab",
    userSelect: "none",
    touchAction: "none",
    "&:active": {
      cursor: "grabbing"
    }
  },
  fabButton: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
      : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "scale(1.08)",
      boxShadow: "0 20px 30px -10px rgba(0,0,0,0.4)"
    },
    "&:focus": {
      outline: "none"
    }
  },
  offlineFab: {
    background: "linear-gradient(135deg, #64748b 0%, #475569 100%)"
  },
  ringFab: {
    animation: "$pulse 1.2s infinite",
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
  },
  "@keyframes pulse": {
    "0%": {
      boxShadow: "0 0 0 0 rgba(22, 163, 74, 0.7)"
    },
    "70%": {
      boxShadow: "0 0 0 16px rgba(22, 163, 74, 0)"
    },
    "100%": {
      boxShadow: "0 0 0 0 rgba(22, 163, 74, 0)"
    }
  },
  cardWidget: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 9999,
    width: 320,
    borderRadius: 20,
    background: theme.palette.type === "dark"
      ? "rgba(15, 23, 42, 0.95)"
      : "rgba(255, 255, 255, 0.96)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"
    }`,
    boxShadow: "0 20px 35px -10px rgba(0,0,0,0.25), 0 10px 15px -5px rgba(0,0,0,0.1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },
  header: {
    padding: theme.spacing(1.5, 2),
    background: theme.palette.type === "dark"
      ? "linear-gradient(90deg, #1e293b 0%, #0f172a 100%)"
      : "linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8
  },
  onlineDot: { background: "#10b981", boxShadow: "0 0 8px #10b981" },
  offlineDot: { background: "#ef4444" },
  connectingDot: { background: "#f59e0b" },
  bodyContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5)
  },
  dialDisplay: {
    background: theme.palette.type === "dark" ? "#020617" : "#f8fafc",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1, 1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 48
  },
  displayText: {
    fontFamily: "monospace",
    fontSize: "1.4rem",
    fontWeight: 700,
    letterSpacing: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  keypadGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1.2),
    margin: "8px 0"
  },
  keyButton: {
    height: 48,
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "#1e293b" : "#f1f5f9",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.palette.type === "dark" ? "#334155" : "#e2e8f0",
      transform: "scale(1.03)"
    },
    "&:active": {
      transform: "scale(0.95)"
    }
  },
  keyNumber: {
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a"
  },
  keyLetters: {
    fontSize: "0.6rem",
    color: theme.palette.text.secondary,
    letterSpacing: 1,
    marginTop: 2
  },
  timerText: {
    fontFamily: "monospace",
    fontSize: "1.6rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#38bdf8" : "#0284c7",
    textAlign: "center",
    margin: "8px 0"
  },
  logConsole: {
    background: "#020617",
    color: "#38bdf8",
    fontFamily: "monospace",
    fontSize: "0.7rem",
    padding: theme.spacing(1),
    borderRadius: 8,
    maxHeight: 110,
    overflowY: "auto",
    marginTop: 4,
    border: "1px solid #1e293b"
  }
}));

const KEYPAD_KEYS = [
  { key: "1", sub: "" },
  { key: "2", sub: "ABC" },
  { key: "3", sub: "DEF" },
  { key: "4", sub: "GHI" },
  { key: "5", sub: "JKL" },
  { key: "6", sub: "MNO" },
  { key: "7", sub: "PQRS" },
  { key: "8", sub: "TUV" },
  { key: "9", sub: "WXYZ" },
  { key: "*", sub: "" },
  { key: "0", sub: "+" },
  { key: "#", sub: "" }
];

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const GlobalSoftphoneWidget = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const {
    registered,
    registering,
    callStatus,
    inCall,
    incoming,
    incomingFrom,
    muted,
    activeNumber,
    callDuration,
    canUseSoftphone,
    userVoip,
    softphoneOpen,
    openSoftphone,
    closeSoftphone,
    makeSoftphoneCall,
    makeAmiCall,
    answerCall,
    rejectCall,
    hangupCall,
    toggleMute,
    sendDTMF,
    voipLogs
  } = useContext(GlobalSoftphoneContext);

  const [dialNumber, setDialNumber] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [planState, setPlanState] = useState(null);
  const [planLoaded, setPlanLoaded] = useState(false);
  const didDragRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const loadPlan = async () => {
      if (!user?.companyId) return;
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (mounted) {
          setPlanState(planConfigs?.plan || null);
          setPlanLoaded(true);
        }
      } catch (_) {
        if (mounted) {
          setPlanState(null);
          setPlanLoaded(true);
        }
      }
    };

    loadPlan();

    const handlePlansChanged = () => {
      loadPlan();
    };

    window.addEventListener("plans:changed", handlePlansChanged);

    return () => {
      mounted = false;
      window.removeEventListener("plans:changed", handlePlansChanged);
    };
  }, [getPlanCompany, user?.companyId]);

  // Carregar posição salva do FAB
  useEffect(() => {
    try {
      const saved = localStorage.getItem(BUTTON_POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
          setButtonPosition(parsed);
        }
      }
    } catch (_) {}
  }, []);

  // Lógica do Drag and Drop no FAB (mouse + touch)
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleStartDrag = (clientX, clientY) => {
    const target = document.getElementById("softphone_fab");
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setButtonPosition({ x: rect.left, y: rect.top });
    isDraggingRef.current = true;
    setIsDragging(true);
    dragOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
    didDragRef.current = false;
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    handleStartDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      handleStartDrag(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!isDraggingRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const target = document.getElementById("softphone_fab");
      if (!target) return;
      const maxX = window.innerWidth - target.offsetWidth;
      const maxY = window.innerHeight - target.offsetHeight;
      let newX = clientX - dragOffsetRef.current.x;
      let newY = clientY - dragOffsetRef.current.y;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      setButtonPosition({ x: newX, y: newY });
      didDragRef.current = true;
    };

    const handleEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);

  useEffect(() => {
    if (buttonPosition && !isDragging) {
      try {
        localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(buttonPosition));
      } catch (_) {}
    }
  }, [buttonPosition, isDragging]);

  const handleKeyPress = (digit) => {
    if (inCall) {
      sendDTMF(digit);
    }
    setDialNumber((prev) => prev + digit);
  };

  const handleBackspace = () => {
    setDialNumber((prev) => prev.slice(0, -1));
  };

  const isWidgetVisible = softphoneOpen || inCall || incoming;

  const fabStyle = buttonPosition
    ? { left: buttonPosition.x, top: buttonPosition.y, bottom: "auto", right: "auto" }
    : { bottom: 12, right: 16, left: "auto", top: "auto" };

  const hasPlanState = Boolean(planState && typeof planState === "object");
  const voipEnabled = planLoaded && hasPlanState && planState?.useVoip === true;

  if (!voipEnabled) {
    return null;
  }

  return (
    <>
      {/* Modal de Chamada Recebida */}
      <Dialog
        open={incoming}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: "center", paddingBottom: 0 }}>
          <PhoneIncoming size={42} color="#16a34a" style={{ marginBottom: 8 }} />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Chamada Recebida
          </Typography>
        </DialogTitle>
        <DialogContent style={{ textAlign: "center" }}>
          <Typography variant="h5" style={{ fontWeight: 700, margin: "8px 0" }}>
            {incomingFrom || "Número Desconhecido"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Ramal tocando no Issabel PABX
          </Typography>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center", paddingBottom: 24 }}>
          <Button
            variant="contained"
            style={{ background: "#16a34a", color: "#fff", padding: "10px 28px", borderRadius: 12 }}
            startIcon={<Phone size={20} />}
            onClick={answerCall}
          >
            Atender
          </Button>
          <Button
            variant="contained"
            color="secondary"
            style={{ padding: "10px 28px", borderRadius: 12 }}
            startIcon={<PhoneOff size={20} />}
            onClick={rejectCall}
          >
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Botão Flutuante (FAB Arrastável) */}
      {!isWidgetVisible && (
        <div
          id="softphone_fab"
          className={classes.fabContainer}
          style={fabStyle}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <Tooltip title={`Softphone Issabel (Ramal: ${userVoip || "Sem ramal"})`}>
            <button
              type="button"
              className={`${classes.fabButton} ${
                incoming ? classes.ringFab : !registered ? classes.offlineFab : ""
              }`}
              onClick={() => {
                if (didDragRef.current) {
                  didDragRef.current = false;
                  return;
                }
                openSoftphone();
              }}
            >
              <Phone size={24} />
            </button>
          </Tooltip>
        </div>
      )}

      {/* Card Flutuante / Widget do Softphone */}
      {isWidgetVisible && (
        <Paper className={classes.cardWidget} elevation={12}>
          {/* Header */}
          <div className={classes.header}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                className={`${classes.statusDot} ${
                  registered ? classes.onlineDot : registering ? classes.connectingDot : classes.offlineDot
                }`}
              />
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {inCall ? "Em Chamada" : "Softphone Issabel"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Ramal: <strong>{userVoip || "Não vinculado"}</strong>
                </Typography>
              </div>
            </div>
            <IconButton size="small" onClick={closeSoftphone}>
              <Minimize2 size={18} />
            </IconButton>
          </div>

          <div className={classes.bodyContent}>
            {/* Status da linha */}
            <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
              Status: <strong>{callStatus}</strong>
            </Typography>

            {/* SE ESTIVER EM CHAMADA */}
            {inCall ? (
              <div style={{ textAlign: "center" }}>
                <Typography variant="body1" style={{ fontWeight: 700, marginTop: 4 }}>
                  {activeNumber || "Chamada Ativa"}
                </Typography>

                <Typography variant="h4" className={classes.timerText}>
                  {formatTime(callDuration)}
                </Typography>

                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 12 }}>
                  <Tooltip title={muted ? "Desmutar Microfone" : "Mutar Microfone"}>
                    <IconButton
                      onClick={toggleMute}
                      style={{
                        background: muted ? "#ef4444" : "#e2e8f0",
                        color: muted ? "#fff" : "#0f172a"
                      }}
                    >
                      {muted ? <MicOff size={20} /> : <Mic size={20} />}
                    </IconButton>
                  </Tooltip>

                  <Button
                    variant="contained"
                    color="secondary"
                    style={{ borderRadius: 12, padding: "8px 24px" }}
                    startIcon={<PhoneOff size={18} />}
                    onClick={hangupCall}
                  >
                    Desligar
                  </Button>
                </div>
              </div>
            ) : (
              /* MODO DISCADOR */
              <>
                {/* Display do Número */}
                <div className={classes.dialDisplay}>
                  <span className={classes.displayText}>
                    {dialNumber || <span style={{ color: "#94a3b8" }}>Digitar número...</span>}
                  </span>
                  {dialNumber.length > 0 && (
                    <IconButton size="small" onClick={handleBackspace}>
                      <Delete size={18} color="#ef4444" />
                    </IconButton>
                  )}
                </div>

                {/* Teclado Numérico */}
                <div className={classes.keypadGrid}>
                  {KEYPAD_KEYS.map((k) => (
                    <div
                      key={k.key}
                      className={classes.keyButton}
                      onClick={() => handleKeyPress(k.key)}
                    >
                      <span className={classes.keyNumber}>{k.key}</span>
                      {k.sub && <span className={classes.keyLetters}>{k.sub}</span>}
                    </div>
                  ))}
                </div>

                {/* Botões de Ação de Discagem */}
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    fullWidth
                    style={{ borderRadius: 10, fontWeight: 700 }}
                    disabled={!canUseSoftphone || !registered || !dialNumber}
                    onClick={() => makeSoftphoneCall(dialNumber)}
                  >
                    WebRTC
                  </Button>
                  <Button
                    variant="outlined"
                    color="default"
                    size="medium"
                    fullWidth
                    style={{ borderRadius: 10, fontWeight: 700 }}
                    disabled={!userVoip || !dialNumber}
                    onClick={() => makeAmiCall(dialNumber)}
                  >
                    Discar AMI
                  </Button>
                </div>

                {/* Alternador de Logs */}
                <div style={{ marginTop: 4 }}>
                  <Button
                    size="small"
                    startIcon={<Activity size={14} />}
                    style={{ fontSize: 11, color: "#64748b" }}
                    onClick={() => setShowLogs((prev) => !prev)}
                  >
                    {showLogs ? "Ocultar Logs do VoIP" : "Mostrar Logs do VoIP"}
                  </Button>
                  {showLogs && (
                    <div className={classes.logConsole}>
                      {voipLogs.length === 0 ? (
                        <span style={{ color: "#64748b" }}>Nenhum log registrado.</span>
                      ) : (
                        voipLogs.map((log) => (
                          <div key={log.id} style={{ marginBottom: 2 }}>
                            <span style={{ color: "#64748b" }}>[{log.time}]</span>{" "}
                            <span
                              style={{
                                color:
                                  log.type === "error"
                                    ? "#f87171"
                                    : log.type === "success"
                                    ? "#4ade80"
                                    : log.type === "warn"
                                    ? "#fbbf24"
                                    : "#38bdf8"
                              }}
                            >
                              {log.text}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Paper>
      )}
    </>
  );
};

export default GlobalSoftphoneWidget;
